import {AxiosRequestConfig} from 'axios';
import { Track } from 'livekit-client';
import {
	LocalRecordingDBInstance,
	RecordingRow,
	localRecordingChunkPath,
	putChunk,
	upsertRecording,
	markRecordingDone,
	writeBlobToOPFS
} from './LocalRecordingStorage';
import {
	startBackgroundUploader,
	resumePendingOnInit,
	enqueueChunkUpload
} from './LocalRecordingUploader';
import { requestPersistentStorageOnce } from '../utils/storagePersistence';

type AuthFetchContext = {
	authAxios: {
		get: (url: string, config?: AxiosRequestConfig) => Promise<{ data: any }>;
		post: (url: string, body?: any, config?: AxiosRequestConfig) => Promise<{ data: any }>;
	}
};

type LocalAudioRecordingOptions = {
	room: any;                        // LiveKit Room
	fetchContext: AuthFetchContext;   // app fetch context (authAxios)
    uploadToken: string;              // Bearer for server calls (long-lived)
	serverTrackId?: string;           // optional server trackId to use immediately (no LiveKit metadata)
	chunkDurationSec?: number;        // default 2s
	batchSize?: number;               // default 50 (server contract)
	audioSources?: Array<any>;        // default [Track.Source.Microphone]
};

export class LocalAudioRecording {
	private room: any;
	private fetchContext: AuthFetchContext;
    private uploadToken: string;
	private chunkDurationSec: number;
	private batchSize: number;
	private initialServerTrackId?: string;
	private allowedAudioSources: Set<any>;

	private ctx?: AudioContext;
	private destination?: MediaStreamAudioDestinationNode;
	private source?: MediaStreamAudioSourceNode;
	private processor?: ScriptProcessorNode;
	private attachedNodes: Map<MediaStreamTrack, MediaStreamAudioSourceNode> = new Map();
	// Keep local recordings resilient to LiveKit track restarts (device change, unmute reacquire, etc.)
	private localTrackRestartHandlers: Map<any, () => void> = new Map();
	private localTrackToMediaTrack: Map<any, MediaStreamTrack | undefined> = new Map();

	private sampleRate = 48000;
	private channels = 1; // mono by default
	private framesPerChunk = 0;
	private writeFrameIndex = 0;
	private chunkBuffer?: Float32Array;
	private frameCursor = 0;
	private running = false;
	private flushChain: Promise<void> = Promise.resolve();

	private trackId?: string;
	private serverTrackId?: string;
	private metadataPollInterval?: ReturnType<typeof setInterval>;
	private nextSeq = 0;

	private onLocalTrackPublished = (pub: any) => {
		try {
			if (!this.allowedAudioSources.has(pub?.source)) return;
			const lkTrack = pub?.audioTrack;
			const track = lkTrack?.mediaStreamTrack as MediaStreamTrack | undefined;
			this.bindLocalTrackRestarts(lkTrack);
			if (track) this.attachLocalAudioTrack(track);
		} catch {}
	};

	private onLocalTrackUnpublished = (pub: any) => {
		try {
			if (!this.allowedAudioSources.has(pub?.source)) return;
			const lkTrack = pub?.audioTrack;
			this.unbindLocalTrackRestarts(lkTrack);
			const track = lkTrack?.mediaStreamTrack as MediaStreamTrack | undefined;
			if (track) this.detachLocalAudioTrack(track);
		} catch {}
	};

	constructor(opts: LocalAudioRecordingOptions) {
		this.room = opts.room;
		this.fetchContext = opts.fetchContext;
    this.uploadToken = opts.uploadToken;
		this.chunkDurationSec = opts.chunkDurationSec ?? 2;
		this.batchSize = opts.batchSize ?? 50;
		this.initialServerTrackId = typeof opts.serverTrackId === 'string' ? opts.serverTrackId.trim() : undefined;
		this.allowedAudioSources = new Set((opts.audioSources && opts.audioSources.length > 0) ? opts.audioSources : [Track.Source.Microphone]);
	}

	public async start(): Promise<void> {
		if (this.running) throw new Error('LocalAudioRecording already running');
		void requestPersistentStorageOnce('local-recording');

		if (this.initialServerTrackId) {
			this.trackId = this.initialServerTrackId;
			this.serverTrackId = this.initialServerTrackId;
		} else {
			const metaTrackId = this.readMetadataTrackId();
			if (metaTrackId) {
				// If metadata delivers the server trackId, keep legacy behavior (local key == server id).
				this.trackId = metaTrackId;
				this.serverTrackId = metaTrackId;
			} else {
				// Start recording immediately; serverTrackId can arrive later via Firestore.
				this.trackId = createLocalRecordingId();
				this.serverTrackId = undefined;
			}
		}
		if (!this.serverTrackId) this.startMetadataPoll();

		// Start background uploader (idempotent)
		startBackgroundUploader({
			onComplete: async (trackId, totalChunksCount) => {
				const rec = await LocalRecordingDBInstance.recordings.get(trackId);
				const token = rec?.uploadToken ?? this.uploadToken;
				const serverTrackId = resolveServerTrackId(rec);
				if (!serverTrackId) throw new Error('server trackId unavailable for /recording/complete');
				const config: AxiosRequestConfig = {
					headers: { Authorization: `Bearer ${token}` },
				};
				try {
					await this.fetchContext.authAxios.post('/recording/complete', {
						trackId: serverTrackId,
						totalChunksCount,
					}, config);
				} catch (e) {
					// Leave recording row; uploader will retry maybeComplete later
					throw e;
				}
			}
		});
		await resumePendingOnInit();

		// If there are existing chunks for this track (resumed), start nextSeq accordingly
		const existing = await LocalRecordingDBInstance.chunks.where('trackId').equals(this.trackId).toArray();
		if (existing.length > 0) {
			this.nextSeq = Math.max(...existing.map(c => c.seq)) + 1;
		} else {
			this.nextSeq = 0;
		}

		// Upsert recording row
			const rec: RecordingRow = {
				trackId: this.trackId,
				createdAt: Date.now(),
				isRecordingDone: false,
				totalChunksCount: null,
				lastBatchNumberFetched: -1,
				uploadToken: this.uploadToken,
				serverTrackId: this.serverTrackId,
				batchSize: this.batchSize,
			};
		await upsertRecording(rec);

		// Audio graph
		this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
		this.sampleRate = this.ctx.sampleRate;
		this.destination = this.ctx.createMediaStreamDestination();
		this.source = this.ctx.createMediaStreamSource(this.destination.stream);

		// ScriptProcessor setup
		this.framesPerChunk = Math.max(1, Math.floor(this.sampleRate * this.chunkDurationSec));
		this.chunkBuffer = new Float32Array(this.framesPerChunk * this.channels);
		this.writeFrameIndex = 0;
		this.frameCursor = 0;
		const bufferSize = 4096;
		this.processor = this.ctx.createScriptProcessor(bufferSize, this.channels, this.channels);
		this.source.connect(this.processor);
		this.processor.connect(this.ctx.destination);
		this.processor.onaudioprocess = (ev: AudioProcessingEvent) => {
			if (!this.running) return;
			try {
				this.handleAudioProcess(ev);
			} catch (e) {
				console.error('onaudioprocess error', e);
			}
		};

		// Attach already published local audio track if present (fallback)
		try {
			const pubs: any = this.room?.localParticipant?.audioTrackPublications;
				if (pubs && typeof pubs.forEach === 'function') {
					let connected = false;
					pubs.forEach((pub: any) => {
						if (!this.allowedAudioSources.has(pub?.source)) return;
						const lkTrack = pub?.audioTrack;
						const track = lkTrack?.mediaStreamTrack as MediaStreamTrack | undefined;
						this.bindLocalTrackRestarts(lkTrack);
						if (!connected && track) {
							this.attachLocalAudioTrack(track);
							connected = true;
						}
					});
				}
		} catch {}

		// Subscribe to local track publish/unpublish
		try {
			const RoomEvent = (this.room as any)?.RoomEvent;
			if (typeof this.room?.on === 'function' && RoomEvent?.LocalTrackPublished) {
				this.room.on(RoomEvent.LocalTrackPublished, this.onLocalTrackPublished);
				if (RoomEvent.LocalTrackUnpublished) {
					this.room.on(RoomEvent.LocalTrackUnpublished, this.onLocalTrackUnpublished);
				}
			} else if (typeof this.room?.on === 'function') {
				// Try string events as fallback
				this.room.on('localTrackPublished', this.onLocalTrackPublished);
				this.room.on('localTrackUnpublished', this.onLocalTrackUnpublished);
			}
		} catch {}

		this.running = true;
	}

	public async stop(): Promise<void> {
		if (!this.running) return;
		this.running = false;
		this.stopMetadataPoll();
		// Flush remaining and wait for any in-flight chunk writes.
		await this.flushChunk().catch(() => {});
		await this.flushChain.catch(() => {});

		this.unbindAllLocalTrackRestarts();
		// Teardown graph
		try { if (this.processor) this.processor.onaudioprocess = null; } catch {}
		try { this.processor?.disconnect(); } catch {}
		try { this.source?.disconnect(); } catch {}
		try { this.destination?.disconnect?.(); } catch {}
		try { await this.ctx?.close(); } catch {}
		this.processor = undefined;
		this.source = undefined;
		this.destination = undefined;
		this.ctx = undefined;

		// Mark recording done and set total chunk count
		if (this.trackId != null) {
			const total = this.nextSeq; // seq is next-to-write; equals total chunks emitted
			await markRecordingDone(this.trackId, total).catch(() => {});
		}

		// Detach listeners
		try {
			const RoomEvent = (this.room as any)?.RoomEvent;
			if (typeof this.room?.off === 'function' && RoomEvent?.LocalTrackPublished) {
				this.room.off(RoomEvent.LocalTrackPublished, this.onLocalTrackPublished);
				if (RoomEvent.LocalTrackUnpublished) {
					this.room.off(RoomEvent.LocalTrackUnpublished, this.onLocalTrackUnpublished);
				}
			} else if (typeof this.room?.off === 'function') {
				this.room.off('localTrackPublished', this.onLocalTrackPublished);
				this.room.off('localTrackUnpublished', this.onLocalTrackUnpublished);
			}
		} catch {}
	}

	public onTrackIdReceived(trackId: string): void {
		void this.applyServerTrackId(trackId);
	}

	public getLocalRecordingId(): string | null {
		return this.trackId ?? null;
	}

	public attachLocalAudioTrack(track: MediaStreamTrack): void {
		if (!this.ctx || !this.destination) return;
		if (this.attachedNodes.has(track)) return;
		try {
			const ms = new MediaStream([track]);
			const node = this.ctx.createMediaStreamSource(ms);
			node.connect(this.destination);
			this.attachedNodes.set(track, node);
		} catch {}
	}

	public detachLocalAudioTrack(track: MediaStreamTrack): void {
		const node = this.attachedNodes.get(track);
		if (node) {
			try { node.disconnect(); } catch {}
			this.attachedNodes.delete(track);
		}
	}

	private bindLocalTrackRestarts(lkTrack: any) {
		if (!lkTrack || typeof lkTrack.on !== 'function') return;
		if (this.localTrackRestartHandlers.has(lkTrack)) return;

		const handler = () => {
			try {
				const next = lkTrack?.mediaStreamTrack as MediaStreamTrack | undefined;
				const prev = this.localTrackToMediaTrack.get(lkTrack);
				if (next === prev) return;
				if (prev) this.detachLocalAudioTrack(prev);
				if (next) this.attachLocalAudioTrack(next);
				this.localTrackToMediaTrack.set(lkTrack, next);
			} catch {}
		};

		this.localTrackToMediaTrack.set(lkTrack, lkTrack.mediaStreamTrack as MediaStreamTrack | undefined);
		try {
			lkTrack.on('restarted', handler);
		} catch {}
		this.localTrackRestartHandlers.set(lkTrack, handler);
	}

	private unbindLocalTrackRestarts(lkTrack: any) {
		if (!lkTrack) return;
		const handler = this.localTrackRestartHandlers.get(lkTrack);
		if (handler) {
			try {
				lkTrack.off?.('restarted', handler);
			} catch {}
			this.localTrackRestartHandlers.delete(lkTrack);
		}
		const prev = this.localTrackToMediaTrack.get(lkTrack);
		this.localTrackToMediaTrack.delete(lkTrack);
		if (prev) this.detachLocalAudioTrack(prev);
	}

	private unbindAllLocalTrackRestarts() {
		for (const [lkTrack, handler] of this.localTrackRestartHandlers.entries()) {
			try {
				lkTrack.off?.('restarted', handler);
			} catch {}
		}
		this.localTrackRestartHandlers.clear();
		for (const track of this.localTrackToMediaTrack.values()) {
			if (track) this.detachLocalAudioTrack(track);
		}
		this.localTrackToMediaTrack.clear();
	}

	private readMetadataTrackId(): string | undefined {
		try {
			const meta = this.room?.localParticipant?.metadata;
			if (meta && typeof meta === 'string' && meta.trim() !== '') return meta.trim();
		} catch {}
		return undefined;
	}

	private startMetadataPoll() {
		if (this.metadataPollInterval) return;
		this.metadataPollInterval = setInterval(() => {
			if (this.serverTrackId) return;
			const meta = this.readMetadataTrackId();
			if (meta) this.onTrackIdReceived(meta);
		}, 500);
	}

	private stopMetadataPoll() {
		if (!this.metadataPollInterval) return;
		clearInterval(this.metadataPollInterval);
		this.metadataPollInterval = undefined;
	}

	private async applyServerTrackId(trackId: string): Promise<void> {
		const clean = typeof trackId === 'string' ? trackId.trim() : '';
		if (!clean) return;
		if (this.serverTrackId === clean) return;
		this.serverTrackId = clean;
		this.stopMetadataPoll();

		if (!this.trackId) return;
		await LocalRecordingDBInstance.recordings.update(this.trackId, { serverTrackId: clean }).catch(() => {});

		// Kick the uploader for any already-persisted chunks that were waiting on serverTrackId.
		try {
			const chunks = await LocalRecordingDBInstance.chunks.where('trackId').equals(this.trackId).toArray();
			for (const c of chunks) {
				enqueueChunkUpload(c.id, this.trackId);
			}
		} catch {}
	}

	private async flushChunk(): Promise<void> {
		if (!this.chunkBuffer || !this.trackId) return;
		const frames = this.writeFrameIndex;
		if (frames <= 0) return;
		const used = frames * this.channels;
		const interleaved = used === this.chunkBuffer.length ? this.chunkBuffer : this.chunkBuffer.slice(0, used);
		// Quantize to 16-bit PCM (little-endian)
		const i16 = new Int16Array(interleaved.length);
		for (let i = 0; i < interleaved.length; i++) {
			const s = Math.max(-1, Math.min(1, interleaved[i]));
			i16[i] = s < 0 ? Math.round(s * 32768) : Math.round(s * 32767);
		}
		const view = new Uint8Array(i16.buffer, i16.byteOffset, i16.byteLength);
		const blob = new Blob([view], { type: 'application/octet-stream' });

		this.writeFrameIndex = 0;
		const seq = this.nextSeq++;
		await this.queueFlush(async () => {
			const opfsPath = localRecordingChunkPath(this.trackId!, seq);
			await writeBlobToOPFS(opfsPath, blob);
			const id = `${this.trackId!}_${seq}`;
			await putChunk({
				id,
				trackId: this.trackId!,
				seq,
				createdAt: Date.now(),
				opfsPath,
				signedUrl: '',
				status: 'pending',
				attemptCount: 0,
			});
			enqueueChunkUpload(id, this.trackId!);
		});
	}

	private queueFlush(task: () => Promise<void>): Promise<void> {
		this.flushChain = this.flushChain
			.then(task)
			.catch((e) => {
				console.warn('chunk flush failed', e);
			});
		return this.flushChain;
	}

	private handleAudioProcess(ev: AudioProcessingEvent) {
		const input = ev.inputBuffer;
		const framesIn = input.length;
		if (!this.chunkBuffer) return;
		const chData: Float32Array[] = [];
		for (let ch = 0; ch < Math.min(this.channels, input.numberOfChannels); ch++) {
			chData.push(input.getChannelData(ch));
		}
		if (chData.length === 0) {
			chData.push(new Float32Array(framesIn));
		}
		while (true) {
			const framesRemainingInChunk = this.framesPerChunk - this.writeFrameIndex;
			const framesToCopy = Math.min(framesRemainingInChunk, framesIn - this.frameCursor);
			for (let i = 0; i < framesToCopy; i++) {
				for (let ch = 0; ch < this.channels; ch++) {
					const sample =
						ch < chData.length
							? chData[ch][this.frameCursor + i]
							: (chData[0]?.[this.frameCursor + i] ?? 0);
					this.chunkBuffer[(this.writeFrameIndex + i) * this.channels + ch] = sample;
				}
				}
				this.writeFrameIndex += framesToCopy;
				this.frameCursor += framesToCopy;
				if (this.writeFrameIndex >= this.framesPerChunk) {
					// fill complete -> async flush
					const full = this.chunkBuffer;
					const trackId = this.trackId!;
					const seqForFlush = this.nextSeq++;
					// allocate new buffer
					this.chunkBuffer = new Float32Array(this.framesPerChunk * this.channels);
					this.writeFrameIndex = 0;
					void this.queueFlush(async () => {
						// Quantize to 16-bit PCM (little-endian)
						const i16 = new Int16Array(full.length);
						for (let i = 0; i < full.length; i++) {
							const s = Math.max(-1, Math.min(1, full[i]));
							i16[i] = s < 0 ? Math.round(s * 32768) : Math.round(s * 32767);
						}
						const view = new Uint8Array(i16.buffer, i16.byteOffset, i16.byteLength);
						const blob = new Blob([view], { type: 'application/octet-stream' });
						const opfsPath = localRecordingChunkPath(trackId, seqForFlush);
						await writeBlobToOPFS(opfsPath, blob);
						const id = `${trackId}_${seqForFlush}`;
						await putChunk({
							id,
							trackId,
							seq: seqForFlush,
							createdAt: Date.now(),
							opfsPath,
							signedUrl: '',
							status: 'pending',
							attemptCount: 0,
						});
						enqueueChunkUpload(id, trackId);
					});
				}
			if (this.frameCursor >= framesIn) {
				this.frameCursor = 0;
				break;
			}
		}
	}
}

function createLocalRecordingId(): string {
	try {
		if (typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function') {
			return `local-${(crypto as any).randomUUID()}`;
		}
	} catch {}
	return `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function resolveServerTrackId(rec: RecordingRow | undefined | null): string | undefined {
	if (!rec) return undefined;
	const candidate = rec.serverTrackId;
	if (candidate && typeof candidate === 'string' && candidate.trim() !== '') return candidate.trim();
	// Legacy: recording rows keyed by server trackId.
	if (rec.trackId && typeof rec.trackId === 'string' && !rec.trackId.startsWith('local-')) return rec.trackId;
	return undefined;
}
